import { NdfOperationFilteredSummary } from '@interfaces/a-organizar/ndfOperationsFilteredSummary';
import { MenuItem } from 'primeng/api';

export type OperationActionType =
  | 'edit'
  | 'remove'
  | 'toHire'
  | 'toSale'
  | 'toQuote'
  | 'revert-hired-to-quoted'
  | 'toSimulated'
  | 'revert-liquidation-to-open'
  | 'attachments'
  | 'mtm'
  | 'create-committee';

export interface OperationAction {
  action: OperationActionType;
  item: NdfOperationFilteredSummary;
  subAction?: 'full' | 'partial' | 'partial-before';
}

export interface TranslationFunction {
  (key: string): string;
}

/**
 * Mixin para gerenciar as ações de menu das operações NDF.
 * Separa a lógica complexa de geração de menus do componente principal.
 * Classe utilitária pura sem dependências do Angular.
 */
export class NdfOperationActionsMixin {
  // Cache para armazenar menus já calculados por operação
  private menuCache = new Map<string, MenuItem[]>();

  constructor(
    private t: TranslationFunction,
    private onAction: (action: OperationAction) => void,
    private shouldShowFullLiquidation: (item: NdfOperationFilteredSummary) => boolean
  ) {}

  /**
   * Gera o menu de ações para uma operação.
   * Usa cache para evitar recálculos desnecessários.
   */
  getMenuActions(item: NdfOperationFilteredSummary): MenuItem[] {
    // Criar chave de cache baseada em propriedades relevantes
    const cacheKey = this.getCacheKey(item);

    // Verificar se já existe no cache
    if (this.menuCache.has(cacheKey)) {
      return this.menuCache.get(cacheKey)!;
    }

    // Gerar menu
    const menu = this.buildMenuActions(item);

    // Armazenar no cache
    this.menuCache.set(cacheKey, menu);

    return menu;
  }

  /**
   * Limpa o cache de menus.
   * Deve ser chamado quando os dados mudam.
   */
  clearCache(): void {
    this.menuCache.clear();
  }

  /**
   * Cria uma chave de cache baseada nas propriedades relevantes da operação.
   */
  private getCacheKey(item: NdfOperationFilteredSummary): string {
    return `${item.id}_${item.operationStatus}_${item.financialStatus}_${item.settlementDate ? '1' : '0'}`;
  }

  /**
   * Constrói o menu de ações baseado no estado da operação.
   */
  private buildMenuActions(item: NdfOperationFilteredSummary): MenuItem[] {
    const actions: MenuItem[] = [];
    const isSimulated = item.operationStatus === 'Simulated';
    const isQuoted = item.operationStatus === 'Quoted';
    const isHired = item.operationStatus === 'Hired';
    const isOpen = item.financialStatus === 'Open';

    // Grupo de Ações Principais
    const mainActions: MenuItem[] = this.buildMainActions(item, isSimulated, isQuoted, isHired);

    if (mainActions.length > 0) {
      actions.push({
        label: 'Ações',
        items: mainActions,
      });
    }

    // Grupo de Liquidação
    if (isHired) {
      if (isOpen) {
        const liquidationItems = this.buildLiquidationActions(item);

        if (liquidationItems.length > 0) {
          if (actions.length > 0) {
            actions.push({ separator: true });
          }
          actions.push({
            label: 'Liquidação',
            items: liquidationItems,
          });
        }
      } else if (item.financialStatus === 'Settled') {
        if (actions.length > 0) {
          actions.push({ separator: true });
        }
        actions.push({
          label: 'Liquidação',
          items: [
            {
              label: this.t('Management.NdfRevertLiquidatedToOpen'),
              icon: 'pi pi-undo',
              command: () => this.onAction({ action: 'revert-liquidation-to-open', item }),
            },
          ],
        });
      }
    }

    return actions;
  }

  /**
   * Constrói as ações principais.
   */
  private buildMainActions(
    item: NdfOperationFilteredSummary,
    isSimulated: boolean,
    isQuoted: boolean,
    isHired: boolean
  ): MenuItem[] {
    const actions: MenuItem[] = [];

    // Editar
    actions.push({
      label: this.t('General.Edit'),
      icon: 'pi pi-file-edit',
      command: () => this.onAction({ action: 'edit', item }),
    });

    // Anexos
    actions.push({
      label: 'Anexos',
      icon: 'pi pi-paperclip',
      command: () => this.onAction({ action: 'attachments', item }),
    });

    // MTM (só para contratadas)
    if (isHired) {
      actions.push({
        label: 'MTM',
        icon: 'pi pi-chart-line',
        command: () => this.onAction({ action: 'mtm', item }),
      });
    }

    actions.push({
      label: 'Criar comitê',
      icon: 'pi pi-users',
      command: () => this.onAction({ action: 'create-committee', item }),
    });

    // Remover
    actions.push({
      label: this.t('General.Delete'),
      icon: 'pi pi-trash',
      command: () => this.onAction({ action: 'remove', item }),
    });

    return actions;
  }

  /**
   * Constrói as ações de liquidação.
   */
  private buildLiquidationActions(item: NdfOperationFilteredSummary): MenuItem[] {
    const actions: MenuItem[] = [];

    // Liquidação Integral no Vencimento
    if (this.shouldShowFullLiquidation(item)) {
      actions.push({
        label: this.t('Management.NdfFullOnMaturity'),
        icon: 'pi pi-dollar',
        command: () => this.onAction({ action: 'toSale', item, subAction: 'full' }),
      });
    }

    // Antes do Vencimento
    actions.push({
      label: this.t('Management.NdfBeforeMaturity'),
      icon: 'pi pi-clock',
      command: () => this.onAction({ action: 'toSale', item, subAction: 'partial-before' }),
    });

    // Liquidação Parcial
    actions.push({
      label: this.t('Management.NdfPartial'),
      icon: 'pi pi-percentage',
      command: () => this.onAction({ action: 'toSale', item, subAction: 'partial' }),
    });

    return actions;
  }

  /**
   * Limpa recursos ao destruir.
   */
  destroy(): void {
    this.clearCache();
  }
}
